import { useEffect, useRef } from "react";

export function useIntersectionAutoPlay(
    videoRef,
    threshold = 0.5
) {
    // Track whether we currently "want" the video to be playing
    const wantPlaying = useRef(false);
    // Track the last intersection state so we only act on real changes
    const lastIntersecting = useRef(null);
    // Track whether a play() call is in flight
    const playInFlight = useRef(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handle = (entries) => {
            for (let entry of entries) {
                const isVisible = entry.intersectionRatio >= threshold;

                // If nothing has changed, do nothing
                if (lastIntersecting.current === isVisible) continue;
                lastIntersecting.current = isVisible;

                if (isVisible) {
                    // Only try to play if we're not already playing or playing in-flight
                    if (video.paused && !playInFlight.current) {
                        wantPlaying.current = true;
                        // Kick off play() and remember its promise
                        playInFlight.current = video
                            .play()
                            .catch((err) => {
                                // If play() fails (e.g. user hasn't interacted yet), clear the flag
                                wantPlaying.current = false;
                                console.warn("Video play() failed:", err);
                            })
                            .finally(() => {
                                // Once play() settles, clear the in-flight marker
                                playInFlight.current = null;
                                // If we lost visibility while that play() was happening, pause()
                                if (!wantPlaying.current && !video.paused) {
                                    video.pause();
                                }
                            });
                    }
                } else {
                    // We want to pause
                    wantPlaying.current = false;

                    // If a play() is still in flight, wait for it to settle, then pause()
                    if (playInFlight.current) {
                        playInFlight.current.finally(() => {
                            if (!wantPlaying.current && !video.paused) {
                                video.pause();
                            }
                        });
                    } else if (!video.paused) {
                        video.pause();
                    }
                }
            }
        };

        const observer = new IntersectionObserver(handle, { threshold });
        observer.observe(video);

        return () => {
            observer.disconnect();
        };
    }, [videoRef, threshold]);
}
